import { useEffect, useRef, useState } from "react";
import { serverNow } from "../state/serverTime.js";

export function formatDuration(ms: number): string {
  if (ms <= 0) return "0s";
  const s = Math.ceil(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return h > 0 ? `${d}d ${h}h` : `${d}d`;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  return `${sec}s`;
}

/** Re-renders four times a second and returns the current server time. */
export function useServerTick(hz = 4): number {
  const [now, setNow] = useState(serverNow());
  useEffect(() => {
    const id = setInterval(() => setNow(serverNow()), 1000 / hz);
    return () => clearInterval(id);
  }, [hz]);
  return now;
}

export interface TimerProps {
  finishesAt: number;
  className?: string;
  onDone?: () => void;
}

/** Countdown rendered from server time; the server, not this component, decides completion (R04). */
export function Timer({ finishesAt, className = "", onDone }: TimerProps) {
  const now = useServerTick();
  const remaining = finishesAt - now;
  const done = remaining <= 0;
  const completed = useRef<number | null>(null);
  useEffect(() => {
    if (!done) { completed.current = null; return; }
    if (completed.current !== finishesAt) { completed.current = finishesAt; onDone?.(); }
  }, [done, finishesAt, onDone]);
  return <span className={`mono ${className}`}>{formatDuration(remaining)}</span>;
}
